import { Link } from "react-router-dom";

/**
 * Service LAB brand assets.
 *
 * Uses the customer-provided raster assets served from /brand/ so the mark
 * on screen is always the exact same artwork as the one the customer sees
 * in email, marketing, invoices, etc.
 *
 * Variants:
 *   - `icon`      : SL monogram only, transparent background; used in the
 *                    sidebar, mobile drawer, portal, auth screens, favicons.
 *   - `full`      : SL monogram + "Service LAB" wordmark on a transparent
 *                    background; used wherever we need the full lock-up
 *                    (email templates, standalone brand headers).
 *   - `full-dark` : same as `full`; the artwork is the same PNG (the
 *                    wordmark is dark and reads on both light and dark
 *                    backgrounds because the design ships with a subtle
 *                    outline). Kept for backward-compat with callers that
 *                    still pass this variant.
 */
export type LogoVariant = "full" | "full-dark" | "icon";

const ICON_SRC = "/brand/service-lab-icon.png";
const FULL_SRC = "/brand/service-lab.png";

export function Logo({
    variant = "full",
    height = 32,
    testId = "brand-logo",
    className,
}: {
    variant?: LogoVariant;
    height?: number;
    testId?: string;
    className?: string;
}) {
    const isIcon = variant === "icon";
    // Full artwork aspect ratio taken from the source PNG (2000 × 750 → 8:3).
    const width = isIcon ? height : Math.round(height * (2000 / 750));
    return (
        <img
            src={isIcon ? ICON_SRC : FULL_SRC}
            alt="Service LAB"
            role="img"
            aria-label="Service LAB"
            data-testid={testId}
            className={className}
            width={width}
            height={height}
            style={{ height, width, display: "block", objectFit: "contain" }}
        />
    );
}

/**
 * Compact brand button used in sidebars: icon + label. Collapsed containers
 * (e.g. rail sidebars) can pass `showText={false}` to hide the wordmark.
 */
export function BrandLink({
    to = "/",
    showText = true,
    testId = "brand-logo-link",
    darkText = false,
}: {
    to?: string;
    showText?: boolean;
    testId?: string;
    darkText?: boolean;
}) {
    return (
        <Link to={to} className="flex items-center gap-2" data-testid={testId} aria-label="Service LAB">
            <Logo variant="icon" height={32} testId={`${testId}-icon`} />
            {showText && (
                <span
                    className="text-sm font-semibold tracking-tight"
                    style={{ color: darkText ? "#0F172A" : "var(--text)" }}
                    data-testid={`${testId}-word`}
                >
                    Service LAB
                </span>
            )}
        </Link>
    );
}
